/*
 * R268~3 · Feb 2026 — Completion dialog for reminders.
 *
 * Every user-initiated "Mark done" now captures a mandatory response
 * note before the reminder is closed. The backend enforces the note
 * at POST /reminders/{rid}/complete → 422 if missing/blank.
 *
 * Usage:
 *   const [target, setTarget] = useState(null);
 *   ...
 *   <button onClick={() => setTarget(reminder)}>Mark done</button>
 *   {target && (
 *     <CompleteReminderDialog
 *       reminder={target}
 *       onClose={() => setTarget(null)}
 *       onDone={() => { setTarget(null); reloadList(); }}
 *     />
 *   )}
 */
import { useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function CompleteReminderDialog({ reminder, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    const n = note.trim();
    if (!n) {
      setError("Please describe what you did.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/reminders/${reminder.id}/complete`, { response_note: n });
      toast.success(reminder.recurrence && reminder.recurrence !== "none"
        ? "Done · next occurrence scheduled"
        : "Marked done");
      onDone?.(n);
    } catch (e) {
      const msg = apiErrorMessage(e, "Failed to complete");
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reminder-complete-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-background border border-border shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-600" /> Mark reminder done
            </h3>
            <div className="text-[11px] text-muted-foreground truncate mt-0.5">
              {reminder?.title || "Reminder"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg leading-none"
            data-testid="reminder-complete-close"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Your response <span className="text-rose-600">*</span>
          </div>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => { setNote(e.target.value); if (error) setError(null); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
            rows={5}
            placeholder="What did you do? What was the outcome? (Ctrl/⌘+Enter to submit)"
            className={`w-full text-xs px-2 py-2 border bg-background resize-y focus:outline-none focus:border-primary ${error ? "border-rose-500" : "border-border"}`}
            data-testid="reminder-complete-note"
            maxLength={2000}
          />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span data-testid="reminder-complete-error">{error || " "}</span>
            <span>{note.length}/2000</span>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center gap-2 bg-muted/20">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2.5 py-1.5 border border-border hover:bg-muted mr-auto"
            data-testid="reminder-complete-cancel"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !note.trim()}
            className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 disabled:opacity-50 font-medium flex items-center gap-1"
            data-testid="reminder-complete-submit"
          >
            <Check className="w-3 h-3" /> {busy ? "Saving…" : "Mark done"}
          </button>
        </div>
      </div>
    </div>
  );
}
